import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  TextField,
  FormGroup,
  FormControlLabel,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

interface IncidentData {
  id: string;
  application_id: string | null;
  configuration_item: string;
  short_description: string;
  resolved: string | null;
  opened: string;
  priority: string;
  brand: string | null;
  mvc: boolean | null;
  platform: string;
  portfolio: string;
  caused_by_platform: string;
  caused_by_portfolio: string;
  caused_by_change: boolean | null;
  change_type: string | null;
  change_process: string | null;
  triggering_change_request: string[];
  observations: string | null;
  root_cause_code: string | null;
  root_cause_sub_code: string | null;
  ttr_seconds: number | null;
  ttd_seconds: number | null;
}

interface OutageData {
  outage_number: string;
  configuration_item: string;
  outage_type: string;
  start_time: string;
  end_time: string;
  duration: string;
  task_number: string;
}

interface P1P2ChangeDashboardProps {
  incidents: IncidentData[];
  outageData: OutageData[];
  loading: boolean;
  error: string | null;
  onRefresh: () => Promise<void>;
}

const P1P2ChangeDashboard: React.FC<P1P2ChangeDashboardProps> = ({ 
  incidents, 
  outageData,
  loading, 
  error,
  onRefresh 
}) => {
  
  // Filter states
  const [timeRange, setTimeRange] = useState<string>('ytd');
  const [startDate, setStartDate] = useState<string>(() => {
    const today = new Date();
    return `${today.getFullYear()}-01-01`;
  });
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [brandFilter, setBrandFilter] = useState<string[]>([]);
  const [impactedPortfolioFilter, setImpactedPortfolioFilter] = useState<string[]>([]);
  const [impactedPlatformFilter, setImpactedPlatformFilter] = useState<string[]>([]);
  const [causedByPlatformFilter, setCausedByPlatformFilter] = useState<string[]>([]);
  const [causedByPortfolioFilter, setCausedByPortfolioFilter] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<string>('monthly');

  // Get unique values from data
  const uniqueBrands = React.useMemo(() => {
    const brands = new Set<string>();
    incidents.forEach(incident => {
      if (incident.brand && incident.brand.trim() !== '') {
        brands.add(incident.brand);
      }
    });
    return Array.from(brands).sort();
  }, [incidents]);



  const uniquePortfolios = React.useMemo(() => {
    const portfolios = new Set<string>();
    incidents.forEach(incident => {
      if (incident.portfolio && incident.portfolio.trim() !== '') {
        portfolios.add(incident.portfolio);
      }
    });
    return Array.from(portfolios).sort();
  }, [incidents]);

  const uniquePlatforms = React.useMemo(() => {
    const platforms = new Set<string>();
    incidents.forEach(incident => {
      if (incident.platform && incident.platform.trim() !== '') {
        platforms.add(incident.platform);
      }
    });
    return Array.from(platforms).sort();
  }, [incidents]);

  const uniqueCausedByPlatforms = React.useMemo(() => {
    const platforms = new Set<string>();
    incidents.forEach(incident => {
      if (incident.caused_by_platform && incident.caused_by_platform.trim() !== '') {
        platforms.add(incident.caused_by_platform);
      }
    });
    return Array.from(platforms).sort();
  }, [incidents]);

  const uniqueCausedByPortfolios = React.useMemo(() => {
    const portfolios = new Set<string>();
    incidents.forEach(incident => {
      if (incident.caused_by_portfolio && incident.caused_by_portfolio.trim() !== '') {
        portfolios.add(incident.caused_by_portfolio);
      }
    });
    return Array.from(portfolios).sort();
  }, [incidents]);

  // Note: Empty array means 'All' selected (no filtering)
  // Non-empty array means specific items selected (filter to those items)

  // Helper function to calculate ISO week number (weeks start on Monday)
  const getISOWeekNumber = (date: Date): { week: number; year: number } => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7; // Make Sunday = 7 instead of 0
    d.setUTCDate(d.getUTCDate() + 4 - dayNum); // Set to nearest Thursday (current week)
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return { week: weekNo, year: d.getUTCFullYear() };
  };

  // Filter incidents based on all selected filters
  const filteredIncidents = React.useMemo(() => {
    return incidents.filter(incident => {
      // Date range filter - skip if no dates are set (timeRange is 'all' or dates are empty)
      if (startDate || endDate) {
        if (incident.resolved) {
          const resolvedDate = new Date(incident.resolved);
          if (startDate) {
            const start = new Date(startDate);
            // Set to beginning of day to include all times on the start date
            start.setHours(0, 0, 0, 0);
            if (resolvedDate < start) {
              return false;
            }
          }
          if (endDate) {
            const end = new Date(endDate);
            // Set to end of day to include all times on the end date
            end.setHours(23, 59, 59, 999);
            if (resolvedDate > end) {
              return false;
            }
          }
        } else {
          // If date range is set but incident has no resolved date, exclude it
          return false;
        }
      }

      // Brand filter
      if (brandFilter.length > 0 && !brandFilter.includes(incident.brand || '')) {
        return false;
      }

      // Impacted Portfolio filter
      if (impactedPortfolioFilter.length > 0 && !impactedPortfolioFilter.includes(incident.portfolio || '')) {
        return false;
      }

      // Impacted Platform filter
      if (impactedPlatformFilter.length > 0 && !impactedPlatformFilter.includes(incident.platform || '')) {
        return false;
      }

      // Caused By Platform filter
      if (causedByPlatformFilter.length > 0 && !causedByPlatformFilter.includes(incident.caused_by_platform || '')) {
        return false;
      }

      // Caused By Portfolio filter
      if (causedByPortfolioFilter.length > 0 && !causedByPortfolioFilter.includes(incident.caused_by_portfolio || '')) {
        return false;
      }

      return true;
    });
  }, [incidents, timeRange, startDate, endDate, brandFilter, impactedPortfolioFilter, impactedPlatformFilter, causedByPlatformFilter, causedByPortfolioFilter]);

  // Helper function to calculate duration in hours from duration string
  const calculateDurationInHours = (durationString: string): number => {
    try {
      // Duration is in format "1970-01-01 HH:MM:SS" where the time represents the duration
      const timePart = durationString.split(' ')[1]; // Get "HH:MM:SS"
      if (!timePart) return 0;
      
      const [hours, minutes, seconds] = timePart.split(':').map(Number);
      return hours + (minutes / 60) + (seconds / 3600);
    } catch (error) {
      console.error('Error parsing duration:', durationString, error);
      return 0;
    }
  };

  // Helper function to format decimal hours into readable format (e.g., "3h 40min")
  const formatHoursToReadable = (decimalHours: number): string => {
    if (decimalHours === 0) return '0h';
    
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    
    if (minutes === 0) {
      return `${hours}h`;
    }
    
    return `${hours}h ${minutes}min`;
  };

  // Calculate outage KPIs
  const outageKPIs = React.useMemo(() => {
    if (!outageData || outageData.length === 0) {
      return {
        totalP1P2OutageHours: 0,
        totalP1P2OutageDueToChangeHours: 0
      };
    }

    // Get all incident IDs from filtered P1/P2 incidents
    const p1p2IncidentIds = new Set(filteredIncidents.map(inc => inc.id));
    
    // Group outages by task_number
    const outagesByTaskNumber = new Map<string, OutageData[]>();
    outageData.forEach(outage => {
      const isP1P2Related = p1p2IncidentIds.has(outage.task_number);
      if (isP1P2Related) {
        if (!outagesByTaskNumber.has(outage.task_number)) {
          outagesByTaskNumber.set(outage.task_number, []);
        }
        outagesByTaskNumber.get(outage.task_number)!.push(outage);
      }
    });

    let totalP1P2OutageHours = 0;
    let totalP1P2OutageDueToChangeHours = 0;

    // For each task number, calculate duration from min start time to max end time
    outagesByTaskNumber.forEach((outages, taskNumber) => {
      if (outages.length === 0) return;

      // Find min start time and max end time
      const times: { start: Date; end: Date }[] = [];

      outages.forEach(outage => {
        try {
          const startTime = new Date(outage.start_time);
          const endTime = new Date(outage.end_time);
          times.push({ start: startTime, end: endTime });
        } catch (error) {
          console.error('Error parsing outage times:', outage, error);
        }
      });

      if (times.length > 0) {
        const minStartTime = new Date(Math.min(...times.map(t => t.start.getTime())));
        const maxEndTime = new Date(Math.max(...times.map(t => t.end.getTime())));

        // Calculate duration in hours
        const durationMs = maxEndTime.getTime() - minStartTime.getTime();
        const durationHours = durationMs / (1000 * 60 * 60); // Convert ms to hours

        totalP1P2OutageHours += durationHours;

        // Check if the related incident was caused by change
        const relatedIncident = filteredIncidents.find(inc => inc.id === taskNumber);
        if (relatedIncident && relatedIncident.caused_by_change) {
          totalP1P2OutageDueToChangeHours += durationHours;
        }
      }
    });

    return {
      totalP1P2OutageHours: Math.round(totalP1P2OutageHours * 10) / 10, // Round to 1 decimal
      totalP1P2OutageDueToChangeHours: Math.round(totalP1P2OutageDueToChangeHours * 10) / 10
    };
  }, [outageData, filteredIncidents]);

  // Aggregate data for Global P1/P2 Trend Chart
  const globalTrendData = React.useMemo(() => {
    if (viewMode === 'monthly') {
      const monthMap = new Map<string, { month: string, p1p2: number, p1p2Change: number, sortKey: string }>();

      filteredIncidents.forEach(incident => {
        if (incident.resolved) {
          const date = new Date(incident.resolved);
          const monthYear = date.toLocaleString('en-US', { month: 'short', year: 'numeric' });
          const sortKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

          if (!monthMap.has(sortKey)) {
            monthMap.set(sortKey, {
              month: monthYear,
              p1p2: 0,
              p1p2Change: 0,
              sortKey
            });
          }

          const monthData = monthMap.get(sortKey)!;
          monthData.p1p2++;
          if (incident.caused_by_change) {
            monthData.p1p2Change++;
          }
        }
      });

      return Array.from(monthMap.values())
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
        .map(({ month, p1p2, p1p2Change }) => ({
          period: month,
          '#P1/P2': p1p2,
          '#P1/P2 Caused By CR': p1p2Change
        }));
    } else {
      // Weekly view (ISO 8601 - weeks start on Monday)
      const weekMap = new Map<string, { week: string, p1p2: number, p1p2Change: number, sortKey: string }>();

      filteredIncidents.forEach(incident => {
        if (incident.resolved) {
          const date = new Date(incident.resolved);
          const { week: weekNumber, year } = getISOWeekNumber(date);
          const weekLabel = `Week ${weekNumber}`;
          const sortKey = `${year}-W${String(weekNumber).padStart(2, '0')}`;

          if (!weekMap.has(sortKey)) {
            weekMap.set(sortKey, {
              week: weekLabel,
              p1p2: 0,
              p1p2Change: 0,
              sortKey
            });
          }

          const weekData = weekMap.get(sortKey)!;
          weekData.p1p2++;
          if (incident.caused_by_change) {
            weekData.p1p2Change++;
          }
        }
      });

      return Array.from(weekMap.values())
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
        .map(({ week, p1p2, p1p2Change }) => ({
          period: week,
          '#P1/P2': p1p2,
          '#P1/P2 Caused By CR': p1p2Change
        }));
    }
  }, [filteredIncidents, viewMode]);

  // Aggregate data for brand-specific charts
  const getBrandTrendData = (brandName: string) => {
    if (viewMode === 'monthly') {
      const monthMap = new Map<string, { month: string, p1p2: number, p1p2Change: number, sortKey: string }>();

      filteredIncidents
        .filter(incident => incident.brand === brandName)
        .forEach(incident => {
          if (incident.resolved) {
            const date = new Date(incident.resolved);
            const monthYear = date.toLocaleString('en-US', { month: 'short', year: 'numeric' });
            const sortKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

            if (!monthMap.has(sortKey)) {
              monthMap.set(sortKey, {
                month: monthYear,
                p1p2: 0,
                p1p2Change: 0,
                sortKey
              });
            }

            const monthData = monthMap.get(sortKey)!;
            monthData.p1p2++;
            if (incident.caused_by_change) {
              monthData.p1p2Change++;
            }
          }
        });

      return Array.from(monthMap.values())
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
        .map(({ month, p1p2, p1p2Change }) => ({
          period: month,
          '#P1/P2': p1p2,
          '#P1/P2 Caused By CR': p1p2Change
        }));
    } else {
      // Weekly view (ISO 8601 - weeks start on Monday)
      const weekMap = new Map<string, { week: string, p1p2: number, p1p2Change: number, sortKey: string }>();

      filteredIncidents
        .filter(incident => incident.brand === brandName)
        .forEach(incident => {
          if (incident.resolved) {
            const date = new Date(incident.resolved);
            const { week: weekNumber, year } = getISOWeekNumber(date);
            const weekLabel = `Week ${weekNumber}`;
            const sortKey = `${year}-W${String(weekNumber).padStart(2, '0')}`;

            if (!weekMap.has(sortKey)) {
              weekMap.set(sortKey, {
                week: weekLabel,
                p1p2: 0,
                p1p2Change: 0,
                sortKey
              });
            }

            const weekData = weekMap.get(sortKey)!;
            weekData.p1p2++;
            if (incident.caused_by_change) {
              weekData.p1p2Change++;
            }
          }
        });

      return Array.from(weekMap.values())
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
        .map(({ week, p1p2, p1p2Change }) => ({
          period: week,
          '#P1/P2': p1p2,
          '#P1/P2 Caused By CR': p1p2Change
        }));
    }
  };

  const oceanTrendData = React.useMemo(() => getBrandTrendData('Ocean'), [filteredIncidents, viewMode]);
  const lsTrendData = React.useMemo(() => getBrandTrendData('L&S'), [filteredIncidents, viewMode]);
  const terminalsTrendData = React.useMemo(() => getBrandTrendData('Terminals'), [filteredIncidents, viewMode]);

  // Calculate MTTR and MTTD trend data (monthly or weekly based on viewMode)
  const mttrMttdTrendData = React.useMemo(() => {
    if (viewMode === 'monthly') {
      // Monthly view
      const monthMap = new Map<string, {
        month: string;
        allTTRSum: number;
        allTTRCount: number;
        allTTDSum: number;
        allTTDCount: number;
        changeTTRSum: number;
        changeTTRCount: number;
        changeTTDSum: number;
        changeTTDCount: number;
        sortKey: string;
      }>();

      filteredIncidents.forEach(incident => {
        if (incident.resolved) {
          const date = new Date(incident.resolved);
          const monthYear = date.toLocaleString('en-US', { month: 'short', year: 'numeric' });
          const sortKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

          if (!monthMap.has(sortKey)) {
            monthMap.set(sortKey, {
              month: monthYear,
              allTTRSum: 0,
              allTTRCount: 0,
              allTTDSum: 0,
              allTTDCount: 0,
              changeTTRSum: 0,
              changeTTRCount: 0,
              changeTTDSum: 0,
              changeTTDCount: 0,
              sortKey
            });
          }

          const monthData = monthMap.get(sortKey)!;
          
          // Process TTR (Time to Resolve)
          if (incident.ttr_seconds !== null && incident.ttr_seconds > 0) {
            const ttrHours = incident.ttr_seconds / 3600; // Convert seconds to hours
            monthData.allTTRSum += ttrHours;
            monthData.allTTRCount++;
            
            if (incident.caused_by_change) {
              monthData.changeTTRSum += ttrHours;
              monthData.changeTTRCount++;
            }
          }

          // Process TTD (Time to Detect)
          if (incident.ttd_seconds !== null && incident.ttd_seconds > 0) {
            const ttdHours = incident.ttd_seconds / 3600; // Convert seconds to hours
            monthData.allTTDSum += ttdHours;
            monthData.allTTDCount++;
            
            if (incident.caused_by_change) {
              monthData.changeTTDSum += ttdHours;
              monthData.changeTTDCount++;
            }
          }
        }
      });

      return Array.from(monthMap.values())
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
        .map(data => ({
          period: data.month,
          allAvgMTTR: data.allTTRCount > 0 ? parseFloat((data.allTTRSum / data.allTTRCount).toFixed(2)) : 0,
          allAvgMTTD: data.allTTDCount > 0 ? parseFloat((data.allTTDSum / data.allTTDCount).toFixed(2)) : 0,
          changeAvgMTTR: data.changeTTRCount > 0 ? parseFloat((data.changeTTRSum / data.changeTTRCount).toFixed(2)) : 0,
          changeAvgMTTD: data.changeTTDCount > 0 ? parseFloat((data.changeTTDSum / data.changeTTDCount).toFixed(2)) : 0,
          allTotalMTTR: parseFloat(data.allTTRSum.toFixed(2)),
          allTotalMTTD: parseFloat(data.allTTDSum.toFixed(2)),
          changeTotalMTTR: parseFloat(data.changeTTRSum.toFixed(2)),
          changeTotalMTTD: parseFloat(data.changeTTDSum.toFixed(2))
        }));
    } else {
      // Weekly view (ISO 8601 - weeks start on Monday)
      const weekMap = new Map<string, {
        week: string;
        allTTRSum: number;
        allTTRCount: number;
        allTTDSum: number;
        allTTDCount: number;
        changeTTRSum: number;
        changeTTRCount: number;
        changeTTDSum: number;
        changeTTDCount: number;
        sortKey: string;
      }>();

      filteredIncidents.forEach(incident => {
        if (incident.resolved) {
          const date = new Date(incident.resolved);
          const { week: weekNumber, year } = getISOWeekNumber(date);
          const weekLabel = `Week ${weekNumber}`;
          const sortKey = `${year}-W${String(weekNumber).padStart(2, '0')}`;

          if (!weekMap.has(sortKey)) {
            weekMap.set(sortKey, {
              week: weekLabel,
              allTTRSum: 0,
              allTTRCount: 0,
              allTTDSum: 0,
              allTTDCount: 0,
              changeTTRSum: 0,
              changeTTRCount: 0,
              changeTTDSum: 0,
              changeTTDCount: 0,
              sortKey
            });
          }

          const weekData = weekMap.get(sortKey)!;
          
          // Process TTR (Time to Resolve)
          if (incident.ttr_seconds !== null && incident.ttr_seconds > 0) {
            const ttrHours = incident.ttr_seconds / 3600; // Convert seconds to hours
            weekData.allTTRSum += ttrHours;
            weekData.allTTRCount++;
            
            if (incident.caused_by_change) {
              weekData.changeTTRSum += ttrHours;
              weekData.changeTTRCount++;
            }
          }

          // Process TTD (Time to Detect)
          if (incident.ttd_seconds !== null && incident.ttd_seconds > 0) {
            const ttdHours = incident.ttd_seconds / 3600; // Convert seconds to hours
            weekData.allTTDSum += ttdHours;
            weekData.allTTDCount++;
            
            if (incident.caused_by_change) {
              weekData.changeTTDSum += ttdHours;
              weekData.changeTTDCount++;
            }
          }
        }
      });

      return Array.from(weekMap.values())
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
        .map(data => ({
          period: data.week,
          allAvgMTTR: data.allTTRCount > 0 ? parseFloat((data.allTTRSum / data.allTTRCount).toFixed(2)) : 0,
          allAvgMTTD: data.allTTDCount > 0 ? parseFloat((data.allTTDSum / data.allTTDCount).toFixed(2)) : 0,
          changeAvgMTTR: data.changeTTRCount > 0 ? parseFloat((data.changeTTRSum / data.changeTTRCount).toFixed(2)) : 0,
          changeAvgMTTD: data.changeTTDCount > 0 ? parseFloat((data.changeTTDSum / data.changeTTDCount).toFixed(2)) : 0,
          allTotalMTTR: parseFloat(data.allTTRSum.toFixed(2)),
          allTotalMTTD: parseFloat(data.allTTDSum.toFixed(2)),
          changeTotalMTTR: parseFloat(data.changeTTRSum.toFixed(2)),
          changeTotalMTTD: parseFloat(data.changeTTDSum.toFixed(2))
        }));
    }
  }, [filteredIncidents, viewMode]);

  // Aggregate data for Impacted Portfolio Bar Chart
  const impactedPortfolioData = React.useMemo(() => {
    const portfolioMap = new Map<string, { portfolio: string, p1p2: number, crP1p2: number }>();

    filteredIncidents.forEach(incident => {
      const portfolio = incident.portfolio || 'Unknown';
      
      if (!portfolioMap.has(portfolio)) {
        portfolioMap.set(portfolio, {
          portfolio,
          p1p2: 0,
          crP1p2: 0
        });
      }

      const data = portfolioMap.get(portfolio)!;
      data.p1p2++;
      if (incident.caused_by_change) {
        data.crP1p2++;
      }
    });

    return Array.from(portfolioMap.values())
      .sort((a, b) => b.p1p2 - a.p1p2)
      .map(item => ({
        portfolio: item.portfolio,
        '#P1/P2': item.p1p2,
        '#CR P1/P2': item.crP1p2
      }));
  }, [filteredIncidents]);

  // Aggregate data for Caused By Portfolio Bar Chart
  const causedByPortfolioData = React.useMemo(() => {
    const portfolioMap = new Map<string, { portfolio: string, p1p2: number, crP1p2: number }>();

    filteredIncidents.forEach(incident => {
      const portfolio = incident.caused_by_portfolio || 'Unknown';
      
      if (!portfolioMap.has(portfolio)) {
        portfolioMap.set(portfolio, {
          portfolio,
          p1p2: 0,
          crP1p2: 0
        });
      }

      const data = portfolioMap.get(portfolio)!;
      data.p1p2++;
      if (incident.caused_by_change) {
        data.crP1p2++;
      }
    });

    return Array.from(portfolioMap.values())
      .sort((a, b) => b.p1p2 - a.p1p2)
      .map(item => ({
        portfolio: item.portfolio,
        '#P1/P2': item.p1p2,
        '#CR P1/P2': item.crP1p2
      }));
  }, [filteredIncidents]);

  // Calculate percentage of P1/P2 due to change
  const percentageP1P2DueToChange = React.useMemo(() => {
    if (filteredIncidents.length === 0) return '0';
    const causedByChangeCount = filteredIncidents.filter(incident => incident.caused_by_change === true).length;
    const percentage = (causedByChangeCount / filteredIncidents.length) * 100;
    return percentage.toFixed(0);
  }, [filteredIncidents]);

  const handleViewChange = (event: React.MouseEvent<HTMLElement>, newView: string | null) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  const handleTimeRangeChange = (range: string) => {
    setTimeRange(range);
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    switch (range) {
      case 'all':
        setStartDate('');
        setEndDate('');
        break;
      case 'last7days':
        const last7 = new Date(today);
        last7.setDate(today.getDate() - 7);
        setStartDate(last7.toISOString().split('T')[0]);
        setEndDate(todayStr);
        break;
      case 'last30days':
        const last30 = new Date(today);
        last30.setDate(today.getDate() - 30);
        setStartDate(last30.toISOString().split('T')[0]);
        setEndDate(todayStr);
        break;
      case 'last90days':
        const last90 = new Date(today);
        last90.setDate(today.getDate() - 90);
        setStartDate(last90.toISOString().split('T')[0]);
        setEndDate(todayStr);
        break;
      case 'ytd':
        setStartDate(today.getFullYear() + '-01-01');
        setEndDate(todayStr);
        break;
      case 'custom':
        // Keep current dates for custom range
        break;
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    const today = new Date();
    setTimeRange('ytd');
    setStartDate(`${today.getFullYear()}-01-01`);
    setEndDate(today.toISOString().split('T')[0]);
    setBrandFilter([]);
    setImpactedPortfolioFilter([]);
    setImpactedPlatformFilter([]);
    setCausedByPlatformFilter([]);
    setCausedByPortfolioFilter([]);
  };

  return (
    <>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!loading && !error && (
        <Container maxWidth="xl">
          <Box sx={{ minHeight: '100vh', py: 3 }}>
            {/* Filters Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
    
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#666' }}>
              Trend Comparison:
            </Typography>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewChange}
              size="small"
              sx={{
                '& .MuiToggleButtonGroup-grouped': {
                  border: '1px solid #d0d0d0',
                  borderRadius: '20px',
                  px: 2.5,
                  py: 0.5,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  '&:not(:first-of-type)': {
                    marginLeft: '8px',
                    borderLeft: '1px solid #d0d0d0',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#0066CC',
                    color: '#fff',
                    borderColor: '#0066CC',
                    '&:hover': {
                      backgroundColor: '#0052A3',
                    },
                  },
                  '&:not(.Mui-selected)': {
                    backgroundColor: '#fff',
                    color: '#333',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="weekly">
                Weekly
              </ToggleButton>
              <ToggleButton value="monthly">
                Monthly
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Filters Section */}
        <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Grid container spacing={1.5} alignItems="flex-end">
          {/* Time Range */}
          <Grid item xs={12} md={1.8}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
              Time Range (Resolved Date)
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={timeRange}
                onChange={(e) => handleTimeRangeChange(e.target.value)}
                sx={{ 
                  backgroundColor: '#fff', 
                  '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b0b0b0' },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="last7days">Last 7 Days</MenuItem>
                <MenuItem value="last30days">Last 30 Days</MenuItem>
                <MenuItem value="last90days">Last 90 Days</MenuItem>
                <MenuItem value="ytd">Year to Date</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Custom Date Range - Show only when Custom is selected */}
          {timeRange === 'custom' && (
            <>
              <Grid item xs={12} md={1.3}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
                  Start Date
                </Typography>
                <TextField
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  size="small"
                  fullWidth
                  sx={{ 
                    backgroundColor: '#fff', 
                    '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8rem' },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#d0d0d0' },
                      '&:hover fieldset': { borderColor: '#b0b0b0' },
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={1.3}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
                  End Date
                </Typography>
                <TextField
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  size="small"
                  fullWidth
                  sx={{ 
                    backgroundColor: '#fff', 
                    '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8rem' },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#d0d0d0' },
                      '&:hover fieldset': { borderColor: '#b0b0b0' },
                    }
                  }}
                />
              </Grid>
            </>
          )}

          {/* Brand */}
          <Grid item xs={12} md={1.1}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
              Brand
            </Typography>
            <FormControl size="small" fullWidth>
              <Select 
                multiple
                displayEmpty
                value={brandFilter} 
                onChange={(e) => {
                  const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                  if (value.includes('all')) {
                    setBrandFilter([]);
                  } else {
                    if (brandFilter.length === 0) {
                      setBrandFilter([value[value.length - 1]]);
                    } else {
                      setBrandFilter(value);
                    }
                  }
                }} 
                sx={{ 
                  backgroundColor: '#fff', 
                  '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b0b0b0' },
                }}
                renderValue={(selected) => selected.length === 0 ? 'All' : selected.join(', ')}
              >
                <MenuItem value="all">
                  <Checkbox checked={brandFilter.length === 0} size="small" />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>All</Typography>
                </MenuItem>
                {uniqueBrands.map(brand => (
                  <MenuItem key={brand} value={brand}>
                    <Checkbox checked={brandFilter.length > 0 && brandFilter.indexOf(brand) > -1} size="small" />
                    <Typography variant="caption">{brand}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Impacted Portfolio */}
          <Grid item xs={12} md={1.25}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
              Impacted Portfolio
            </Typography>
            <FormControl size="small" fullWidth>
              <Select 
                multiple
                displayEmpty
                value={impactedPortfolioFilter} 
                onChange={(e) => {
                  const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                  if (value.includes('all')) {
                    setImpactedPortfolioFilter([]);
                  } else {
                    if (impactedPortfolioFilter.length === 0) {
                      setImpactedPortfolioFilter([value[value.length - 1]]);
                    } else {
                      setImpactedPortfolioFilter(value);
                    }
                  }
                }} 
                sx={{ 
                  backgroundColor: '#fff', 
                  '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b0b0b0' },
                }}
                renderValue={(selected) => selected.length === 0 ? 'All' : selected.join(', ')}
              >
                <MenuItem value="all">
                  <Checkbox checked={impactedPortfolioFilter.length === 0} size="small" />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>All</Typography>
                </MenuItem>
                {uniquePortfolios.map(portfolio => (
                  <MenuItem key={portfolio} value={portfolio}>
                    <Checkbox checked={impactedPortfolioFilter.length > 0 && impactedPortfolioFilter.indexOf(portfolio) > -1} size="small" />
                    <Typography variant="caption">{portfolio}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Impacted Platform */}
          <Grid item xs={12} md={1.25}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
              Impacted Platform
            </Typography>
            <FormControl size="small" fullWidth>
              <Select 
                multiple
                displayEmpty
                value={impactedPlatformFilter} 
                onChange={(e) => {
                  const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                  if (value.includes('all')) {
                    setImpactedPlatformFilter([]);
                  } else {
                    if (impactedPlatformFilter.length === 0) {
                      setImpactedPlatformFilter([value[value.length - 1]]);
                    } else {
                      setImpactedPlatformFilter(value);
                    }
                  }
                }} 
                sx={{ 
                  backgroundColor: '#fff', 
                  '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b0b0b0' },
                }}
                renderValue={(selected) => selected.length === 0 ? 'All' : selected.join(', ')}
              >
                <MenuItem value="all">
                  <Checkbox checked={impactedPlatformFilter.length === 0} size="small" />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>All</Typography>
                </MenuItem>
                {uniquePlatforms.map(platform => (
                  <MenuItem key={platform} value={platform}>
                    <Checkbox checked={impactedPlatformFilter.length > 0 && impactedPlatformFilter.indexOf(platform) > -1} size="small" />
                    <Typography variant="caption">{platform}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Caused By Platform */}
          <Grid item xs={12} md={1.25}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
              Caused By Platform
            </Typography>
            <FormControl size="small" fullWidth>
              <Select 
                multiple
                displayEmpty
                value={causedByPlatformFilter} 
                onChange={(e) => {
                  const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                  if (value.includes('all')) {
                    setCausedByPlatformFilter([]);
                  } else {
                    if (causedByPlatformFilter.length === 0) {
                      setCausedByPlatformFilter([value[value.length - 1]]);
                    } else {
                      setCausedByPlatformFilter(value);
                    }
                  }
                }} 
                sx={{ 
                  backgroundColor: '#fff', 
                  '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b0b0b0' },
                }}
                renderValue={(selected) => selected.length === 0 ? 'All' : selected.join(', ')}
              >
                <MenuItem value="all">
                  <Checkbox checked={causedByPlatformFilter.length === 0} size="small" />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>All</Typography>
                </MenuItem>
                {uniqueCausedByPlatforms.map(platform => (
                  <MenuItem key={platform} value={platform}>
                    <Checkbox checked={causedByPlatformFilter.length > 0 && causedByPlatformFilter.indexOf(platform) > -1} size="small" />
                    <Typography variant="caption">{platform}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Caused By Portfolio */}
          <Grid item xs={12} md={1.25}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
              Caused By Portfolio
            </Typography>
            <FormControl size="small" fullWidth>
              <Select 
                multiple
                displayEmpty
                value={causedByPortfolioFilter} 
                onChange={(e) => {
                  const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                  if (value.includes('all')) {
                    setCausedByPortfolioFilter([]);
                  } else {
                    if (causedByPortfolioFilter.length === 0) {
                      setCausedByPortfolioFilter([value[value.length - 1]]);
                    } else {
                      setCausedByPortfolioFilter(value);
                    }
                  }
                }} 
                sx={{ 
                  backgroundColor: '#fff', 
                  '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b0b0b0' },
                }}
                renderValue={(selected) => selected.length === 0 ? 'All' : selected.join(', ')}
              >
                <MenuItem value="all">
                  <Checkbox checked={causedByPortfolioFilter.length === 0} size="small" />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>All</Typography>
                </MenuItem>
                {uniqueCausedByPortfolios.map(portfolio => (
                  <MenuItem key={portfolio} value={portfolio}>
                    <Checkbox checked={causedByPortfolioFilter.length > 0 && causedByPortfolioFilter.indexOf(portfolio) > -1} size="small" />
                    <Typography variant="caption">{portfolio}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Clear Selection Button */}
          <Grid item xs={12} md={1.2}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: 'transparent', userSelect: 'none' }}>
              .
            </Typography>
            <Button 
              variant="outlined" 
              size="small" 
              fullWidth
              onClick={clearAllFilters}
              sx={{ 
                height: '33px', 
                fontSize: '0.7rem', 
                py: 0.5,
                borderColor: '#d0d0d0',
                color: '#666',
                '&:hover': {
                  borderColor: '#b0b0b0',
                  backgroundColor: '#f5f5f5',
                }
              }}
            >
              Clear Selection
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Global P1/P2 Trend Chart */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
          Global P1/P2 Trend
        </Typography>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={globalTrendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="period" 
              tick={{ fontSize: 12 }}
              label={{ value: viewMode === 'monthly' ? 'Monthly View' : 'Weekly View', position: 'insideBottom', offset: -5, fontSize: 12 }}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              label={{ value: '#P1/P2', angle: -90, position: 'insideLeft', fontSize: 12 }}
            />
            <Tooltip />
            <Legend 
              verticalAlign="top" 
              height={36}
              iconType="circle"
              wrapperStyle={{ fontSize: '12px' }}
            />
            <Line 
              type="monotone" 
              dataKey="#P1/P2" 
              stroke="#1e3a8a" 
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
              label={{ position: 'top', fontSize: 11, fill: '#1e3a8a', fontWeight: 'bold' }}
            />
            <Line 
              type="monotone" 
              dataKey="#P1/P2 Caused By CR" 
              stroke="#ea580c" 
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
              label={{ position: 'top', fontSize: 11, fill: '#ea580c', fontWeight: 'bold' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      {/* Portfolio Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Ocean Chart */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
              Ocean
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={oceanTrendData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="period" 
                  tick={{ fontSize: 10 }}
                  label={{ value: viewMode === 'monthly' ? 'Monthly View' : 'Weekly View', position: 'insideBottom', offset: -5, fontSize: 10 }}
                />
                <YAxis 
                  tick={{ fontSize: 10 }}
                  label={{ value: '#P1/P2', angle: -90, position: 'insideLeft', fontSize: 10 }}
                />
                <Tooltip />
                <Legend 
                  verticalAlign="top" 
                  height={30}
                  iconType="circle"
                  wrapperStyle={{ fontSize: '10px' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="#P1/P2" 
                  stroke="#1e3a8a" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  label={{ position: 'top', fontSize: 10, fill: '#1e3a8a', fontWeight: 'bold' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="#P1/P2 Caused By CR" 
                  stroke="#ea580c" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  label={{ position: 'top', fontSize: 10, fill: '#ea580c', fontWeight: 'bold' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* L&S Chart */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
              L&S
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={lsTrendData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="period" 
                  tick={{ fontSize: 10 }}
                  label={{ value: viewMode === 'monthly' ? 'Monthly View' : 'Weekly View', position: 'insideBottom', offset: -5, fontSize: 10 }}
                />
                <YAxis 
                  tick={{ fontSize: 10 }}
                  label={{ value: '#P1/P2', angle: -90, position: 'insideLeft', fontSize: 10 }}
                />
                <Tooltip />
                <Legend 
                  verticalAlign="top" 
                  height={30}
                  iconType="circle"
                  wrapperStyle={{ fontSize: '10px' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="#P1/P2" 
                  stroke="#1e3a8a" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  label={{ position: 'top', fontSize: 10, fill: '#1e3a8a', fontWeight: 'bold' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="#P1/P2 Caused By CR" 
                  stroke="#ea580c" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  label={{ position: 'top', fontSize: 10, fill: '#ea580c', fontWeight: 'bold' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Terminals Chart */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
              Terminals
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={terminalsTrendData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="period" 
                  tick={{ fontSize: 10 }}
                  label={{ value: viewMode === 'monthly' ? 'Monthly View' : 'Weekly View', position: 'insideBottom', offset: -5, fontSize: 10 }}
                />
                <YAxis 
                  tick={{ fontSize: 10 }}
                  label={{ value: '#P1/P2', angle: -90, position: 'insideLeft', fontSize: 10 }}
                />
                <Tooltip />
                <Legend 
                  verticalAlign="top" 
                  height={30}
                  iconType="circle"
                  wrapperStyle={{ fontSize: '10px' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="#P1/P2" 
                  stroke="#1e3a8a" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  label={{ position: 'top', fontSize: 10, fill: '#1e3a8a', fontWeight: 'bold' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="#P1/P2 Caused By CR" 
                  stroke="#ea580c" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  label={{ position: 'top', fontSize: 10, fill: '#ea580c', fontWeight: 'bold' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* MTTR & MTTD Trends Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              MTTR & MTTD Trends
            </Typography>
            
            <Grid container spacing={3}>
              {/* MTTD Chart */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
                    Total MTTD (Hours)
                  </Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={mttrMttdTrendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="period" 
                        tick={{ fontSize: 12 }}
                        label={{ value: viewMode === 'monthly' ? 'Monthly View' : 'Weekly View', position: 'insideBottom', offset: -5, fontSize: 12 }}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        label={{ value: 'Hours', angle: -90, position: 'insideLeft', fontSize: 12 }}
                      />
                      <Tooltip 
                        contentStyle={{ fontSize: '0.85rem' }}
                        formatter={(value: number) => [`${value} hrs`, '']}
                      />
                      <Legend 
                        verticalAlign="top" 
                        height={36}
                        iconType="circle"
                        wrapperStyle={{ fontSize: '12px' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="allTotalMTTD"
                        stroke="#1e3a8a" 
                        name="All Incidents"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        label={{ position: 'top', fontSize: 11, fill: '#1e3a8a', fontWeight: 'bold' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="changeTotalMTTD"
                        stroke="#ea580c" 
                        name="Due to Change"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        label={{ position: 'top', fontSize: 11, fill: '#ea580c', fontWeight: 'bold' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* MTTR Chart */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
                    Total MTTR (Hours)
                  </Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={mttrMttdTrendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="period" 
                        tick={{ fontSize: 12 }}
                        label={{ value: viewMode === 'monthly' ? 'Monthly View' : 'Weekly View', position: 'insideBottom', offset: -5, fontSize: 12 }}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        label={{ value: 'Hours', angle: -90, position: 'insideLeft', fontSize: 12 }}
                      />
                      <Tooltip 
                        contentStyle={{ fontSize: '0.85rem' }}
                        formatter={(value: number) => [`${value} hrs`, '']}
                      />
                      <Legend 
                        verticalAlign="top" 
                        height={36}
                        iconType="circle"
                        wrapperStyle={{ fontSize: '12px' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="allTotalMTTR"
                        stroke="#1e3a8a" 
                        name="All Incidents"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        label={{ position: 'top', fontSize: 11, fill: '#1e3a8a', fontWeight: 'bold' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="changeTotalMTTR"
                        stroke="#ea580c" 
                        name="Due to Change"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        label={{ position: 'top', fontSize: 11, fill: '#ea580c', fontWeight: 'bold' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Bar Charts Row */}
      <Grid container spacing={3}>
        {/* Impacted Portfolio Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
              Impacted Portfolio
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={impactedPortfolioData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="portfolio" 
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                  tick={{ fontSize: 11 }}
                  label={{ value: 'Impacted Portfolio', position: 'insideBottom', offset: -80, fontSize: 12 }}
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  label={{ value: '# P1/P2', angle: -90, position: 'insideLeft', fontSize: 12 }}
                />
                <Tooltip />
                <Legend 
                  verticalAlign="top" 
                  height={36}
                  iconType="circle"
                  wrapperStyle={{ fontSize: '11px' }}
                />
                <Bar dataKey="#P1/P2" stackId="a" fill="#1e3a8a" label={{ position: 'inside', fill: '#fff', fontSize: 11, fontWeight: 'bold' }} />
                <Bar dataKey="#CR P1/P2" stackId="a" fill="#ea580c" label={{ position: 'top', fill: '#000', fontSize: 11, fontWeight: 'bold' }} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Caused By Portfolio Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
              Caused By Portfolio
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={causedByPortfolioData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="portfolio" 
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                  tick={{ fontSize: 11 }}
                  label={{ value: 'Caused By Portfolio', position: 'insideBottom', offset: -80, fontSize: 12 }}
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  label={{ value: '# P1/P2', angle: -90, position: 'insideLeft', fontSize: 12 }}
                />
                <Tooltip />
                <Legend 
                  verticalAlign="top" 
                  height={36}
                  iconType="circle"
                  wrapperStyle={{ fontSize: '11px' }}
                />
                <Bar dataKey="#P1/P2" stackId="a" fill="#1e3a8a" label={{ position: 'inside', fill: '#fff', fontSize: 11, fontWeight: 'bold' }} />
                <Bar dataKey="#CR P1/P2" stackId="a" fill="#ea580c" label={{ position: 'top', fill: '#000', fontSize: 11, fontWeight: 'bold' }} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
      </Box>
    </Container>
      )}
    </>
  );
};

export default P1P2ChangeDashboard;